import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, FileDown } from 'lucide-react';
import { PageLayout } from '@/shared/layout/PageLayout';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useAuth } from '@/core/auth/AuthContext';
import { usePermission } from '@/core/permissions/usePermission';
import { useTeamsList } from './hooks/useTeamsList';
import { TeamsListKPI } from './components/TeamsListKPI';
import { TeamsListFilters } from './components/TeamsListFilters';
import { TeamsListTable } from './components/TeamsListTable';
import type { TeamListItem } from './types/teamsList.types';

export default function TeamsListPage() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';
  const { can } = usePermission();
  const canCreate = can('teams' as any, 'create' as any);
  
  const { 
    teams, kpis, isLoading, filters, setFilters, 
    pagination, setPagination, deleteTeam 
  } = useTeamsList(companyId);

  const [teamToDelete, setTeamToDelete] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('all');

  const handleExport = () => {
    console.log('Exporting teams list...');
  };

  const handleDeleteConfirm = async () => {
    if (teamToDelete) {
      try {
        await deleteTeam(teamToDelete);
        setTeamToDelete(null);
      } catch (error) {
      }
    }
  };

  return (
    <PageLayout
      title="Đội thi công"
      description="Quản lý và phân bổ các đội nhóm thi công / bảo dưỡng"
    >
      <div className="flex flex-col h-full gap-6">
        
        {/* KPI Section */}
        <TeamsListKPI kpis={kpis} />

        {/* Toolbar Section (Tabs & Actions) */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            {/* Left: Navigation Tabs */}
            <div className="flex items-center gap-1 border-b border-slate-100 lg:border-none pb-2 lg:pb-0">
              {['all', 'thi-cong', 'bao-duong'].map((tab) => {
                const label = tab === 'all' ? 'Tất cả' : tab === 'thi-cong' ? 'Thi công' : 'Bảo dưỡng';
                return (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`relative px-4 py-2 sm:text-sm text-xs font-normal transition-all ${
                      activeTab === tab
                        ? 'text-emerald-700 after:absolute after:bottom-[-8px] lg:after:bottom-[-12px] after:left-0 after:h-0.5 after:w-full after:bg-emerald-600'
                        : 'text-slate-400 hover:text-slate-600'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            
            {/* Right: Actions */}
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={handleExport} className="h-9 gap-2 sm:text-sm text-xs border-slate-200">
                <FileDown className="w-4 h-4" />
                Xuất danh sách
              </Button>
              {canCreate && (
                <Button onClick={() => navigate('/teams/new')} className="h-9 gap-2 sm:text-sm text-xs bg-emerald-600 hover:bg-emerald-700 text-white border-none">
                  <Plus className="w-4 h-4" />
                  Thêm đội
                </Button>
              )}
            </div>
          </div>

          {/* Filters */}
          <TeamsListFilters 
            filters={filters} 
            onFilterChange={setFilters} 
            onReset={() => setFilters({ search: '', category: 'all', status: 'all', department: 'all' })}
          />
        </div>

        {/* List Section */}
        <div className="flex-1 overflow-y-auto min-h-0">
          <TeamsListTable 
            items={teams}
            isLoading={isLoading}
            onView={(team) => navigate(`/teams/${team.id}`)}
            onEdit={(id) => navigate(`/teams/edit/${id}`)}
            onDelete={setTeamToDelete}
          />
        </div>
      </div>

      <ConfirmDialog
        isOpen={!!teamToDelete}
        title="Xác nhận giải thể team"
        message="Hành động này sẽ giải thể đội nhóm và gỡ bỏ các liên kết hiện tại. Bạn có chắc chắn muốn tiếp tục?"
        confirmText="Giải thể ngay"
        isDestructive
        onConfirm={handleDeleteConfirm}
        onClose={() => setTeamToDelete(null)}
      />
    </PageLayout>
  );
}
